import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    IoVideocam,
    IoKeypad,
    IoRadioButtonOn,
    IoTimeOutline,
    IoShieldCheckmarkOutline,
    IoEllipsisVertical,
    IoPlayCircle,
    IoClose,
    IoInformationCircleOutline,
    IoSettingsOutline,
    IoCheckmarkCircle,
    IoEyeOutline,
    IoCalendarOutline,
    IoListOutline,
    IoPersonOutline,
    IoArrowBack,
    IoCarOutline,
    IoWalkOutline,
    IoLockOpenOutline
} from 'react-icons/io5';
import Hls from 'hls.js';
import { useAuth } from '../context/AuthContext';
import { db } from '../firebaseConfig';
import {
    collection,
    doc,
    getDocs,
    onSnapshot,
    setDoc,
    addDoc,
    serverTimestamp
} from 'firebase/firestore';
import KondoSidebar from '../components/Shared/KondoSidebar';
import KondoBottomNav from '../components/Shared/KondoBottomNav';
import KondoBrandHeader from '../components/Shared/KondoBrandHeader';
import './SeguridadScreen.css';

// Lista de cámaras por defecto (4 cámaras para Centro de Control en Desktop)
const DEFAULT_CAMERAS = [
    {
        id: 'cam-1',
        name: 'Caseta Norte - Entrada Principal (Webcam)',
        url: 'http://192.168.3.134:8888/caseta/index.m3u8',
        fallbackUrl: 'https://playertest.longtailvideo.com/adaptive/bipbop/bipbop.m3u8',
        locationTag: 'Transmisión en vivo (Cámara Laptop)',
        maxSeconds: 60,
        maxMinutesPerHour: 10,
        maxHoursPerDay: 4,
        maxDaysPerWeek: 7,
        allowedRoles: ['propietario', 'inquilino', 'comite', 'vigilante', 'admin'],
        description: 'Monitoreo vehicular de accesos en caseta principal. Diseñada para verificar llegadas de paquetería y visitas.'
    },
    {
        id: 'cam-2',
        name: 'Terminal de Accesos - Reconocimiento Facial',
        url: 'http://192.168.3.134:8888/terminal/index.m3u8',
        fallbackUrl: 'https://demo.unified-streaming.com/k8s/features/stable/video/tears-of-steel/tears-of-steel.ism/.m3u8',
        locationTag: 'Terminal Biométrica de Caseta',
        maxSeconds: 45,
        maxMinutesPerHour: 5,
        maxHoursPerDay: 2,
        maxDaysPerWeek: 7,
        allowedRoles: ['propietario', 'inquilino', 'comite', 'vigilante', 'admin'],
        description: 'Cámara frontal del tótem de reconocimiento facial para corroborar autorizaciones de acceso vehicular y peatonal.'
    },
    {
        id: 'cam-3',
        name: 'Caseta Sur - Salida Vehicular',
        url: 'http://192.168.3.134:8888/terminal/index.m3u8',
        fallbackUrl: 'https://playertest.longtailvideo.com/adaptive/bipbop/bipbop.m3u8',
        locationTag: 'Carril de Salida y Pluma B',
        maxSeconds: 60,
        maxMinutesPerHour: 10,
        maxHoursPerDay: 4,
        maxDaysPerWeek: 7,
        allowedRoles: ['propietario', 'comite', 'vigilante', 'admin'],
        description: 'Supervisión de carril de salida vehicular y apertura automática de tag RFID.'
    },
    {
        id: 'cam-4',
        name: 'Alberca y Terraza Club',
        url: 'http://192.168.3.134:8888/terminal/index.m3u8',
        fallbackUrl: 'https://demo.unified-streaming.com/k8s/features/stable/video/tears-of-steel/tears-of-steel.ism/.m3u8',
        locationTag: 'Amenidad Principal - Área Común',
        maxSeconds: 30,
        maxMinutesPerHour: 5,
        maxHoursPerDay: 1,
        maxDaysPerWeek: 7,
        allowedRoles: ['propietario', 'inquilino', 'comite', 'admin'],
        description: 'Monitoreo de aforo en alberca y área de asadores para verificación de disponibilidad.'
    }
];

// Opciones de Roles
const AVAILABLE_ROLES = [
    { key: 'propietario', label: 'Propietarios' },
    { key: 'inquilino', label: 'Inquilinos' },
    { key: 'vigilante', label: 'Vigilantes' },
    { key: 'comite', label: 'Comité' },
    { key: 'admin', label: 'Administrador' }
];

// Helper para obtener el consumo acumulado de un usuario en la última hora (en segundos)
const getHourlyUsageSeconds = (condoId, userIdentifier, camId) => {
    try {
        const key = `centinela_usage_${condoId}_${userIdentifier}_${camId}`;
        const data = JSON.parse(localStorage.getItem(key) || '[]');
        const oneHourAgo = Date.now() - (60 * 60 * 1000);
        // Filtrar solo los vistos en los últimos 60 minutos
        const recent = data.filter(item => item.timestamp > oneHourAgo);
        localStorage.setItem(key, JSON.stringify(recent));
        return recent.reduce((sum, item) => sum + (item.seconds || 0), 0);
    } catch (e) {
        return 0;
    }
};

// Helper para registrar consumo
const recordUsageSeconds = (condoId, userIdentifier, camId, seconds) => {
    try {
        const key = `centinela_usage_${condoId}_${userIdentifier}_${camId}`;
        const data = JSON.parse(localStorage.getItem(key) || '[]');
        data.push({ timestamp: Date.now(), seconds });
        localStorage.setItem(key, JSON.stringify(data));
    } catch (e) {}
};

// Subcomponente de video individual
const LiveCameraPlayer = ({
    cam,
    isActive,
    isAdmin,
    hasAccess,
    onOpenRules,
    onOpenConfig,
    onRequestReconnect
}) => {
    const videoRef = useRef(null);
    const [isLive, setIsLive] = useState(false);
    const [timeLeft, setTimeLeft] = useState(cam.maxSeconds || 60);
    const [isTimeExpired, setIsTimeExpired] = useState(false);
    const [reloadKey, setReloadKey] = useState(0);
    const [showMenu, setShowMenu] = useState(false);
    const menuRef = useRef(null);

    useEffect(() => {
        setTimeLeft(cam.maxSeconds || 60);
        setIsTimeExpired(false);
    }, [cam.maxSeconds, isActive]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (menuRef.current && !menuRef.current.contains(event.target)) {
                setShowMenu(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    useEffect(() => {
        if (!isActive || isTimeExpired) return;

        const video = videoRef.current;
        if (!video) return;

        let hls;

        if (Hls.isSupported()) {
            hls = new Hls({
                enableWorker: true,
                lowLatencyMode: true,
                backBufferLength: 0,
                maxBufferLength: 2,
                liveSyncDurationCount: 1,
            });

            hls.loadSource(cam.url);
            hls.attachMedia(video);

            hls.on(Hls.Events.MANIFEST_PARSED, () => {
                video.play().then(() => setIsLive(true)).catch(() => {});
            });

            hls.on(Hls.Events.ERROR, (event, data) => {
                if (data.fatal) {
                    setIsLive(false);
                    if (cam.fallbackUrl && hls.url !== cam.fallbackUrl) {
                        hls.loadSource(cam.fallbackUrl);
                    } else {
                        hls.destroy();
                    }
                }
            });
        } else if (video.canPlayType('application/vnd.apple.mpegurl')) {
            video.src = cam.url;
            video.addEventListener('loadedmetadata', () => {
                video.play().then(() => setIsLive(true)).catch(() => {});
            });
        }

        return () => {
            if (hls) hls.destroy();
        };
    }, [cam.url, cam.fallbackUrl, isActive, reloadKey, isTimeExpired]);

    useEffect(() => {
        if (isAdmin || !isLive || isTimeExpired || !isActive) return;

        const timer = setInterval(() => {
            setTimeLeft((prev) => {
                if (prev <= 1) {
                    clearInterval(timer);
                    setIsTimeExpired(true);
                    setIsLive(false);
                    if (videoRef.current) videoRef.current.pause();
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);

        return () => clearInterval(timer);
    }, [isLive, isTimeExpired, isAdmin, isActive, reloadKey]);

    return (
        <div style={{
            background: 'white',
            borderRadius: '20px',
            overflow: 'hidden',
            boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)',
            border: '1px solid #E2E8F0',
            display: 'flex',
            flexDirection: 'column'
        }}>
            {/* Contenedor del Video */}
            <div style={{ position: 'relative', width: '100%', background: '#090D16', aspectRatio: '16/9' }}>
                {!isActive && (
                    <div style={{
                        width: '100%',
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        background: 'radial-gradient(circle at center, #1E293B 0%, #090D16 100%)',
                        color: 'white',
                        padding: '20px',
                        textAlign: 'center',
                        gap: '12px'
                    }}>
                        <div style={{
                            width: '56px',
                            height: '56px',
                            borderRadius: '50%',
                            background: hasAccess ? 'rgba(126, 102, 252, 0.15)' : 'rgba(239, 68, 68, 0.15)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: hasAccess ? '#7E66FC' : '#EF4444',
                            border: `1px solid ${hasAccess ? 'rgba(126, 102, 252, 0.3)' : 'rgba(239, 68, 68, 0.3)'}`
                        }}>
                            <IoVideocam size={28} />
                        </div>
                        <div>
                            <h4 style={{ margin: 0, fontSize: '0.95rem', fontWeight: '600' }}>{cam.name}</h4>
                            <span style={{ fontSize: '0.75rem', color: '#94A3B8' }}>
                                {hasAccess ? 'Transmisión encriptada bajo demanda' : 'Acceso no autorizado para tu rol'}
                            </span>
                        </div>
                        {hasAccess ? (
                            <button
                                onClick={() => onOpenRules(cam)}
                                style={{
                                    background: 'linear-gradient(135deg, #7E66FC 0%, #6366F1 100%)',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '12px',
                                    padding: '10px 20px',
                                    fontSize: '0.85rem',
                                    fontWeight: '600',
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px',
                                    boxShadow: '0 4px 12px rgba(126, 102, 252, 0.35)'
                                }}
                            >
                                <IoPlayCircle size={20} /> Conectar en Vivo
                            </button>
                        ) : (
                            <span style={{
                                background: 'rgba(239, 68, 68, 0.2)',
                                color: '#F87171',
                                padding: '6px 14px',
                                borderRadius: '8px',
                                fontSize: '0.8rem',
                                fontWeight: '600'
                            }}>
                                Sin Autorización de Visualización
                            </span>
                        )}
                    </div>
                )}

                {isActive && !isTimeExpired && (
                    <video
                        ref={videoRef}
                        muted
                        autoPlay
                        playsInline
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                )}

                {isActive && isTimeExpired && (
                    <div style={{
                        width: '100%',
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        background: 'linear-gradient(180deg, #0F172A 0%, #1E293B 100%)',
                        color: 'white',
                        padding: '20px',
                        textAlign: 'center',
                        gap: '12px'
                    }}>
                        <IoTimeOutline size={36} color="#94A3B8" />
                        <div>
                            <h4 style={{ margin: 0, fontSize: '1rem', fontWeight: '600' }}>Sesión Finalizada por Privacidad</h4>
                            <p style={{ margin: '4px 0 0', fontSize: '0.8rem', color: '#94A3B8' }}>
                                Límite continuo de {cam.maxSeconds || 60} segundos cumplido
                            </p>
                        </div>
                        <button
                            onClick={() => onRequestReconnect(cam)}
                            style={{
                                background: '#7E66FC',
                                color: 'white',
                                border: 'none',
                                borderRadius: '10px',
                                padding: '8px 16px',
                                fontSize: '0.85rem',
                                fontWeight: '600',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '6px'
                            }}
                        >
                            <IoPlayCircle size={16} /> Reanudar Vista
                        </button>
                    </div>
                )}

                {/* Badges superiores */}
                {isActive && !isTimeExpired && (
                    <div style={{
                        position: 'absolute',
                        top: '12px',
                        left: '12px',
                        background: 'rgba(15, 23, 42, 0.75)',
                        backdropFilter: 'blur(6px)',
                        color: isLive ? '#10B981' : '#F59E0B',
                        padding: '4px 10px',
                        borderRadius: '8px',
                        fontSize: '0.75rem',
                        fontWeight: '600',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px'
                    }}>
                        <IoRadioButtonOn size={14} color={isLive ? '#10B981' : '#F59E0B'} />
                        <span>{isLive ? 'EN VIVO (CÁMARA REAL)' : 'CONECTANDO...'}</span>
                    </div>
                )}

                {isActive && !isTimeExpired && isLive && !isAdmin && (
                    <div style={{
                        position: 'absolute',
                        top: '12px',
                        right: '12px',
                        background: timeLeft <= 10 ? 'rgba(239, 68, 68, 0.85)' : 'rgba(15, 23, 42, 0.75)',
                        backdropFilter: 'blur(6px)',
                        color: 'white',
                        padding: '4px 10px',
                        borderRadius: '8px',
                        fontSize: '0.75rem',
                        fontWeight: '700',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px'
                    }}>
                        <IoTimeOutline size={14} />
                        <span>{`${Math.floor(timeLeft / 60)}:${timeLeft % 60 < 10 ? '0' + (timeLeft % 60) : timeLeft % 60}`}</span>
                    </div>
                )}

                {isActive && isAdmin && (
                    <div style={{
                        position: 'absolute',
                        top: '12px',
                        right: '12px',
                        background: 'rgba(126, 102, 252, 0.85)',
                        backdropFilter: 'blur(6px)',
                        color: 'white',
                        padding: '4px 10px',
                        borderRadius: '8px',
                        fontSize: '0.75rem',
                        fontWeight: '600',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px'
                    }}>
                        <IoShieldCheckmarkOutline size={14} />
                        <span>Admin (Sin Límite)</span>
                    </div>
                )}
            </div>

            {/* Pie de Información y Menú de 3 Puntos */}
            <div style={{ padding: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', position: 'relative' }}>
                <div>
                    <h4 style={{ margin: 0, fontSize: '0.95rem', color: '#0F172A' }}>{cam.name}</h4>
                    <span style={{ fontSize: '0.75rem', color: '#64748B' }}>{cam.locationTag}</span>
                </div>

                <div ref={menuRef} style={{ position: 'relative' }}>
                    <button
                        onClick={() => setShowMenu(!showMenu)}
                        title="Opciones de cámara"
                        style={{
                            background: showMenu ? '#E2E8F0' : '#F1F5F9',
                            border: 'none',
                            borderRadius: '10px',
                            width: '36px',
                            height: '36px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'pointer',
                            color: '#475569',
                            transition: 'all 0.2s'
                        }}
                    >
                        <IoEllipsisVertical size={18} />
                    </button>

                    {showMenu && (
                        <div style={{
                            position: 'absolute',
                            right: 0,
                            bottom: '44px',
                            background: 'white',
                            borderRadius: '12px',
                            boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.15), 0 8px 10px -6px rgba(0, 0, 0, 0.1)',
                            border: '1px solid #E2E8F0',
                            padding: '6px',
                            minWidth: '190px',
                            zIndex: 100,
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '4px'
                        }}>
                            <button
                                onClick={() => {
                                    setShowMenu(false);
                                    onOpenRules(cam, false);
                                }}
                                style={{
                                    background: 'none',
                                    border: 'none',
                                    textAlign: 'left',
                                    padding: '8px 12px',
                                    borderRadius: '8px',
                                    fontSize: '0.85rem',
                                    color: '#1E293B',
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px',
                                    fontWeight: '500'
                                }}
                                onMouseEnter={(e) => e.currentTarget.style.background = '#F8FAFC'}
                                onMouseLeave={(e) => e.currentTarget.style.background = 'none'}
                            >
                                <IoEyeOutline size={16} color="#64748B" />
                                <span>Ver Reglas y Límites</span>
                            </button>

                            {isAdmin && (
                                <button
                                    onClick={() => {
                                        setShowMenu(false);
                                        onOpenConfig(cam);
                                    }}
                                    style={{
                                        background: 'none',
                                        border: 'none',
                                        textAlign: 'left',
                                        padding: '8px 12px',
                                        borderRadius: '8px',
                                        fontSize: '0.85rem',
                                        color: '#7E66FC',
                                        cursor: 'pointer',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '8px',
                                        fontWeight: '600'
                                    }}
                                    onMouseEnter={(e) => e.currentTarget.style.background = '#F5F3FF'}
                                    onMouseLeave={(e) => e.currentTarget.style.background = 'none'}
                                >
                                    <IoSettingsOutline size={16} color="#7E66FC" />
                                    <span>Configurar Reglas</span>
                                </button>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

function CentinelaScreen() {
    const navigate = useNavigate();
    const { userData, currentUser } = useAuth();
    const [actionMessage, setActionMessage] = useState('');
    const [isOpening, setIsOpening] = useState(false);
    const [quotaExceededMessage, setQuotaExceededMessage] = useState('');

    const condoId = userData?.condominioId || 'SOMOS_DEFAULT';
    // Mapeo ESTRICTO y EXCLUYENTE de Roles:
    const getEffectiveUserRole = () => {
        const rawRole = (userData?.rol || '').toLowerCase().trim();
        const rawTipo = (userData?.tipoResidente || '').toLowerCase().trim();
        const email = (userData?.email || '').toLowerCase().trim();

        if (['admin', 'superuser', 'masteruser'].includes(rawRole)) return 'admin';
        if (rawRole === 'comite') return 'comite';
        if (rawRole === 'vigilante') return 'vigilante';
        
        // Si explícitamente es inquilino en tipoResidente, rol o correo
        if (rawTipo === 'inquilino' || rawRole === 'inquilino' || email.includes('inquilino') || email.includes('residente020')) {
            return 'inquilino';
        }

        if (rawTipo === 'propietario' || rawRole === 'propietario' || rawRole === 'residente') {
            return 'propietario';
        }

        return 'propietario';
    };

    const effectiveRole = getEffectiveUserRole();
    const isAdmin = effectiveRole === 'admin' || effectiveRole === 'comite';

    // Obtener casa/depto exacto del usuario
    const userHouseNumber = userData?.numeroCasa || userData?.casaNumero || userData?.unidadResidente || userData?.depto || (userData?.email ? userData.email.split('@')[0] : 'S/N');
    const userIdentifier = currentUser?.uid || userData?.email || userData?.nombre || 'user_demo';

    // Cargar cámaras aisladas por condominio
    const storageKey = `centinela_camaras_${condoId}`;
    const bitacoraStorageKey = `centinela_bitacora_${condoId}`;

    const getStoredCameras = () => {
        try {
            const saved = localStorage.getItem(storageKey);
            if (saved) return JSON.parse(saved);
        } catch (e) {}
        return DEFAULT_CAMERAS;
    };

    const [cameraList, setCameraList] = useState(getStoredCameras);
    const [activeCamIds, setActiveCamIds] = useState({});

    // Sincronización en tiempo real de cámaras desde Firestore
    useEffect(() => {
        if (!condoId) return;

        const camColRef = collection(db, `condominios/${condoId}/camaras_centinela`);
        const unsubscribe = onSnapshot(camColRef, (snapshot) => {
            if (!snapshot.empty) {
                const loaded = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
                setCameraList(loaded);
                localStorage.setItem(storageKey, JSON.stringify(loaded));
            } else {
                setCameraList(getStoredCameras());
            }
        }, (err) => {
            console.warn("Error escuchando cámaras de Centinela:", err);
            setCameraList(getStoredCameras());
        });

        return () => unsubscribe();
    }, [condoId]);

    // Cargar bitácora desde localStorage
    const getStoredBitacora = () => {
        try {
            const saved = localStorage.getItem(bitacoraStorageKey);
            if (saved) return JSON.parse(saved);
        } catch (e) {}
        return [];
    };

    // Modales y Estados de Configuración
    const [selectedCameraForRules, setSelectedCameraForRules] = useState(null);
    const [isConnectIntent, setIsConnectIntent] = useState(false);
    const [selectedCameraForConfig, setSelectedCameraForConfig] = useState(null);
    const [showBitacoraModal, setShowBitacoraModal] = useState(false);
    const [bitacoraLogs, setBitacoraLogs] = useState(getStoredBitacora);

    // Estados para Gestión de Accesos Remotos
    const [showDoorsConfigModal, setShowDoorsConfigModal] = useState(false);
    const [selectedDoorForConfig, setSelectedDoorForConfig] = useState(null);
    const [doorConfigForm, setDoorConfigForm] = useState({
        name: '',
        locationTag: '',
        gpioPin: 17,
        allowedRoles: ['propietario', 'inquilino', 'comite', 'vigilante', 'admin']
    });

    // Formulario de edición de reglas de cámaras para el Admin
    const [configForm, setConfigForm] = useState({
        name: '',
        maxSeconds: 60,
        maxMinutesPerHour: 10,
        maxHoursPerDay: 4,
        maxDaysPerWeek: 7,
        allowedRoles: ['propietario', 'inquilino', 'comite', 'vigilante', 'admin'],
        description: ''
    });

    // Intentar conectar o reanudar sesión verificando cupo por hora
    const handleAttemptConnect = (camera, isInitialPopup = true) => {
        // Si no es admin, validar que no haya excedido el límite por hora
        if (!isAdmin) {
            const maxSecondsPerHour = (camera.maxMinutesPerHour || 10) * 60;
            const currentUsage = getHourlyUsageSeconds(condoId, userIdentifier, camera.id);

            if (currentUsage + (camera.maxSeconds || 60) > maxSecondsPerHour && currentUsage >= maxSecondsPerHour) {
                setQuotaExceededMessage(`Has alcanzado el límite de ${camera.maxMinutesPerHour || 10} minutos permitidos por hora para esta cámara. Vuelve a intentarlo más tarde.`);
                return;
            }
        }

        if (isInitialPopup) {
            setSelectedCameraForRules(camera);
            setIsConnectIntent(true);
        } else {
            // Reanudar directamente registrando nuevo segmento en la bitácora
            startSessionAndLog(camera);
        }
    };

    // Registrar inicio de sesión en Bitácora y acumular consumo
    const startSessionAndLog = async (camera) => {
        const segs = camera.maxSeconds || 60;

        // 1. Activar stream
        setActiveCamIds(prev => ({ ...prev, [camera.id]: true }));

        // 2. Registrar consumo por hora
        recordUsageSeconds(condoId, userIdentifier, camera.id, segs);

        // 3. Crear entrada de bitácora
        const newLog = {
            id: 'log-' + Date.now(),
            camaraId: camera.id,
            camaraNombre: camera.name,
            usuarioNombre: userData?.nombre || userData?.email || 'Usuario Demo',
            casaDepto: userHouseNumber.startsWith('Casa') || userHouseNumber.startsWith('Depto') ? userHouseNumber : `Casa ${userHouseNumber}`,
            rol: effectiveRole,
            tipoResidente: userData?.tipoResidente || (effectiveRole === 'inquilino' ? 'Inquilino' : 'Propietario'),
            maxSecondsAsignados: segs,
            fechaHora: new Date().toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit', second: '2-digit' }),
            fecha: new Date().toLocaleDateString('es-MX')
        };

        const updatedLogs = [newLog, ...getStoredBitacora()];
        localStorage.setItem(bitacoraStorageKey, JSON.stringify(updatedLogs));
        setBitacoraLogs(updatedLogs);

        try {
            await addDoc(collection(db, `condominios/${condoId}/bitacora_centinela`), {
                ...newLog,
                usuarioId: currentUser?.uid || 'anonimo',
                condominioId: condoId,
                timestamp: serverTimestamp()
            });
        } catch (err) {}
    };

    // Confirmar conexión desde el modal de reglas
    const handleConfirmConnect = () => {
        if (!selectedCameraForRules) return;
        startSessionAndLog(selectedCameraForRules);
        setSelectedCameraForRules(null);
    };

    // Abrir visor de bitácora
    const handleOpenBitacora = () => {
        setBitacoraLogs(getStoredBitacora());
        setShowBitacoraModal(true);
    };

    // Abrir modal de configuración para Admin
    const handleOpenConfigModal = (camera) => {
        setSelectedCameraForConfig(camera);
        setConfigForm({
            name: camera.name,
            maxSeconds: camera.maxSeconds || 60,
            maxMinutesPerHour: camera.maxMinutesPerHour || 10,
            maxHoursPerDay: camera.maxHoursPerDay || 4,
            maxDaysPerWeek: camera.maxDaysPerWeek || 7,
            allowedRoles: Array.isArray(camera.allowedRoles) ? camera.allowedRoles : ['propietario', 'inquilino', 'comite', 'vigilante', 'admin'],
            description: camera.description || ''
        });
    };

    const handleToggleRole = (roleKey) => {
        setConfigForm(prev => {
            const exists = prev.allowedRoles.includes(roleKey);
            const updated = exists
                ? prev.allowedRoles.filter(r => r !== roleKey)
                : [...prev.allowedRoles, roleKey];
            return { ...prev, allowedRoles: updated };
        });
    };

    // Guardar cambios en configuración
    const handleSaveCameraConfig = async (e) => {
        e.preventDefault();

        const updatedCam = {
            ...selectedCameraForConfig,
            name: configForm.name,
            maxSeconds: Math.min(Math.max(Number(configForm.maxSeconds) || 1, 1), 3600),
            maxMinutesPerHour: Math.min(Math.max(Number(configForm.maxMinutesPerHour) || 1, 1), 60),
            maxHoursPerDay: Math.min(Math.max(Number(configForm.maxHoursPerDay) || 1, 1), 24),
            maxDaysPerWeek: Math.min(Math.max(Number(configForm.maxDaysPerWeek) || 1, 1), 7),
            allowedRoles: configForm.allowedRoles,
            description: configForm.description
        };

        const updatedList = cameraList.map(c => c.id === updatedCam.id ? updatedCam : c);
        setCameraList(updatedList);
        localStorage.setItem(storageKey, JSON.stringify(updatedList));

        // Guardado en Firestore
        try {
            await setDoc(doc(db, `condominios/${condoId}/camaras_centinela`, selectedCameraForConfig.id), {
                ...updatedCam,
                condominioId: condoId,
                updatedAt: serverTimestamp()
            }, { merge: true });
        } catch (error) {}

        setSelectedCameraForConfig(null);
    };

    return (
        <div className="ks-page">
            <KondoSidebar activeRoute="/seguridad" />
            <main className="ks-main">
                <div className="ks-header-sticky-wrapper">
                    <KondoBrandHeader showBack={false} />
                </div>
                <div className="ks-scroll-area">
                    <div className="seguridad-container" style={{ padding: '15px 20px', maxWidth: '1280px', margin: '0 auto', width: '100%', boxSizing: 'border-box' }}>
                        
                        {/* Header Banner Estándar (Estilo Comunicaciones / Licitaciones) */}
                        <div className="app-header-sticky" style={{ padding: 0, marginBottom: '20px' }}>
                            <div className="kondo-banner-standard" style={{ background: 'linear-gradient(135deg, #4f46e5 0%, #3730a3 100%)', margin: 0 }}>
                                <div className="banner-standard-content">
                                    <button onClick={() => navigate('/seguridad')} className="banner-back-btn-standard">
                                        <IoArrowBack size={24} />
                                    </button>
                                    <div className="banner-standard-text">
                                        <h2>Centinela</h2>
                                        <p>Monitoreo de caseta en tiempo real</p>
                                    </div>
                                </div>
                                <div className="banner-standard-actions" style={{ display: 'flex', alignItems: 'center', gap: '12px', zIndex: 2 }}>
                                    {isAdmin && (
                                        <button
                                            onClick={handleOpenBitacora}
                                            style={{
                                                background: 'white',
                                                color: '#4F46E5',
                                                border: 'none',
                                                borderRadius: '12px',
                                                padding: '8px 16px',
                                                fontSize: '0.85rem',
                                                fontWeight: '700',
                                                cursor: 'pointer',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '6px',
                                                boxShadow: '0 4px 10px rgba(0, 0, 0, 0.15)',
                                                transition: 'all 0.2s ease'
                                            }}
                                        >
                                            <IoListOutline size={18} />
                                            <span>Bitácora</span>
                                        </button>
                                    )}
                                    <IoVideocam size={22} color="white" style={{ opacity: 0.8 }} />
                                </div>
                                <IoVideocam className="banner-standard-icon-decor" />
                            </div>
                        </div>

                        {/* Grid Responsivo de Cámaras (4 en Desktop, 1 por fila en Mobile) */}
                        <div style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(auto-fit, minmax(360px, 1fr))',
                            gap: '24px'
                        }}>
                            {cameraList.map((cam) => {
                                const hasAccess = isAdmin || (
                                    Array.isArray(cam.allowedRoles) &&
                                    cam.allowedRoles.includes(effectiveRole)
                                );
                                return (
                                    <LiveCameraPlayer
                                        key={cam.id}
                                        cam={cam}
                                        isActive={!!activeCamIds[cam.id]}
                                        isAdmin={isAdmin}
                                        hasAccess={hasAccess}
                                        onOpenRules={(camera) => handleAttemptConnect(camera, true)}
                                        onOpenConfig={(camera) => handleOpenConfigModal(camera)}
                                        onRequestReconnect={(camera) => handleAttemptConnect(camera, false)}
                                    />
                                );
                            })}
                        </div>
                    </div>
                </div>
                <KondoBottomNav activeRoute="/seguridad" />
            </main>

            {/* Modal de Límite de Cuota Excedido */}
            {quotaExceededMessage && (
                <div style={{
                    position: 'fixed',
                    inset: 0,
                    backgroundColor: 'rgba(15, 23, 42, 0.65)',
                    backdropFilter: 'blur(4px)',
                    zIndex: 99999,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '20px'
                }}>
                    <div style={{
                        background: 'white',
                        borderRadius: '20px',
                        maxWidth: '400px',
                        width: '100%',
                        padding: '24px',
                        textAlign: 'center',
                        boxShadow: '0 20px 25px -5px rgba(0,0,0,0.2)'
                    }}>
                        <IoTimeOutline size={48} color="#EF4444" style={{ marginBottom: '12px' }} />
                        <h4 style={{ margin: 0, fontSize: '1.1rem', color: '#0F172A', fontWeight: '700' }}>
                            Cupo por Hora Excedido
                        </h4>
                        <p style={{ margin: '10px 0 20px', fontSize: '0.85rem', color: '#64748B', lineHeight: '1.4' }}>
                            {quotaExceededMessage}
                        </p>
                        <button
                            onClick={() => setQuotaExceededMessage('')}
                            style={{
                                width: '100%',
                                background: '#7E66FC',
                                color: 'white',
                                border: 'none',
                                borderRadius: '12px',
                                padding: '12px',
                                fontWeight: '600',
                                fontSize: '0.9rem',
                                cursor: 'pointer'
                            }}
                        >
                            Entendido
                        </button>
                    </div>
                </div>
            )}

            {/* ========================================================= */}
            {/* 1. POPUP DE REGLAMENTO DE PRIVACIDAD / CONFIRMACIÓN */}
            {/* ========================================================= */}
            {selectedCameraForRules && (
                <div style={{
                    position: 'fixed',
                    inset: 0,
                    backgroundColor: 'rgba(15, 23, 42, 0.65)',
                    backdropFilter: 'blur(4px)',
                    zIndex: 9999,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '20px'
                }}>
                    <div style={{
                        background: 'white',
                        borderRadius: '24px',
                        maxWidth: '460px',
                        width: '100%',
                        overflow: 'hidden',
                        boxShadow: '0 20px 25px -5px rgba(0,0,0,0.2)'
                    }}>
                        <div style={{
                            background: 'linear-gradient(135deg, #1E293B 0%, #0F172A 100%)',
                            color: 'white',
                            padding: '20px',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center'
                        }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                <IoShieldCheckmarkOutline size={24} color="#7E66FC" />
                                <h3 style={{ margin: 0, fontSize: '1.05rem', fontWeight: '700' }}>Reglamento de Privacidad</h3>
                            </div>
                            <button
                                onClick={() => setSelectedCameraForRules(null)}
                                style={{ background: 'none', border: 'none', color: '#94A3B8', cursor: 'pointer' }}
                            >
                                <IoClose size={22} />
                            </button>
                        </div>

                        <div style={{ padding: '24px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
                            <div>
                                <span style={{ fontSize: '0.75rem', fontWeight: '700', color: '#7E66FC', textTransform: 'uppercase' }}>Cámara Seleccionada</span>
                                <h4 style={{ margin: '2px 0 6px', fontSize: '1.1rem', color: '#0F172A' }}>{selectedCameraForRules.name}</h4>
                                <p style={{ margin: 0, fontSize: '0.85rem', color: '#64748B', lineHeight: '1.4' }}>
                                    {selectedCameraForRules.description}
                                </p>
                            </div>

                            <div style={{
                                background: '#F8FAFC',
                                borderRadius: '16px',
                                padding: '16px',
                                border: '1px solid #E2E8F0',
                                display: 'grid',
                                gridTemplateColumns: '1fr 1fr',
                                gap: '12px'
                            }}>
                                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                                    <IoTimeOutline size={18} color="#7E66FC" style={{ flexShrink: 0, marginTop: '2px' }} />
                                    <div>
                                        <h5 style={{ margin: 0, fontSize: '0.78rem', color: '#1E293B', fontWeight: '700' }}>Límite Continuo</h5>
                                        <p style={{ margin: '2px 0 0', fontSize: '0.8rem', color: '#475569', fontWeight: '600' }}>
                                            {selectedCameraForRules.maxSeconds || 60} segundos
                                        </p>
                                    </div>
                                </div>

                                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                                    <IoInformationCircleOutline size={18} color="#7E66FC" style={{ flexShrink: 0, marginTop: '2px' }} />
                                    <div>
                                        <h5 style={{ margin: 0, fontSize: '0.78rem', color: '#1E293B', fontWeight: '700' }}>Límite por Hora</h5>
                                        <p style={{ margin: '2px 0 0', fontSize: '0.8rem', color: '#475569', fontWeight: '600' }}>
                                            {selectedCameraForRules.maxMinutesPerHour || 10} minutos / hr
                                        </p>
                                    </div>
                                </div>

                                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                                    <IoTimeOutline size={18} color="#6366F1" style={{ flexShrink: 0, marginTop: '2px' }} />
                                    <div>
                                        <h5 style={{ margin: 0, fontSize: '0.78rem', color: '#1E293B', fontWeight: '700' }}>Límite por Día</h5>
                                        <p style={{ margin: '2px 0 0', fontSize: '0.8rem', color: '#475569', fontWeight: '600' }}>
                                            {selectedCameraForRules.maxHoursPerDay || 4} horas / día
                                        </p>
                                    </div>
                                </div>

                                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                                    <IoCalendarOutline size={18} color="#6366F1" style={{ flexShrink: 0, marginTop: '2px' }} />
                                    <div>
                                        <h5 style={{ margin: 0, fontSize: '0.78rem', color: '#1E293B', fontWeight: '700' }}>Límite por Semana</h5>
                                        <p style={{ margin: '2px 0 0', fontSize: '0.8rem', color: '#475569', fontWeight: '600' }}>
                                            {selectedCameraForRules.maxDaysPerWeek || 7} días / semana
                                        </p>
                                    </div>
                                </div>
                            </div>

                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', background: 'rgba(16, 185, 129, 0.1)', padding: '10px 14px', borderRadius: '12px' }}>
                                <IoCheckmarkCircle size={18} color="#10B981" />
                                <span style={{ fontSize: '0.78rem', color: '#065F46', fontWeight: '500' }}>
                                    Tu sesión se registrará de forma transparente en la bitácora de seguridad.
                                </span>
                            </div>

                            <div style={{ display: 'flex', gap: '10px', marginTop: '4px' }}>
                                <button
                                    onClick={() => setSelectedCameraForRules(null)}
                                    style={{
                                        flex: 1,
                                        background: '#F1F5F9',
                                        color: '#475569',
                                        border: 'none',
                                        borderRadius: '12px',
                                        padding: '12px',
                                        fontWeight: '600',
                                        fontSize: '0.9rem',
                                        cursor: 'pointer'
                                    }}
                                >
                                    Cerrar
                                </button>
                                {isConnectIntent && (
                                    <button
                                        onClick={handleConfirmConnect}
                                        style={{
                                            flex: 1.5,
                                            background: '#7E66FC',
                                            color: 'white',
                                            border: 'none',
                                            borderRadius: '12px',
                                            padding: '12px',
                                            fontWeight: '600',
                                            fontSize: '0.9rem',
                                            cursor: 'pointer',
                                            boxShadow: '0 4px 10px rgba(126, 102, 252, 0.3)'
                                        }}
                                    >
                                        Aceptar y Conectar
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* ========================================================= */}
            {/* 2. MODAL DE CONFIGURACIÓN DE REGLAS DE CÁMARA (ADMIN/COMITÉ) */}
            {/* ========================================================= */}
            {selectedCameraForConfig && (
                <div style={{
                    position: 'fixed',
                    inset: 0,
                    backgroundColor: 'rgba(15, 23, 42, 0.65)',
                    backdropFilter: 'blur(4px)',
                    zIndex: 9999,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '20px'
                }}>
                    <div style={{
                        background: 'white',
                        borderRadius: '24px',
                        maxWidth: '520px',
                        width: '100%',
                        maxHeight: '90vh',
                        overflowY: 'auto',
                        boxShadow: '0 20px 25px -5px rgba(0,0,0,0.2)'
                    }}>
                        <div style={{
                            background: 'linear-gradient(135deg, #4F46E5 0%, #7E66FC 100%)',
                            color: 'white',
                            padding: '20px',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center'
                        }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                <IoSettingsOutline size={24} />
                                <h3 style={{ margin: 0, fontSize: '1.05rem', fontWeight: '700' }}>Configuración de Reglas de Cámara</h3>
                            </div>
                            <button
                                onClick={() => setSelectedCameraForConfig(null)}
                                style={{ background: 'none', border: 'none', color: 'white', cursor: 'pointer' }}
                            >
                                <IoClose size={22} />
                            </button>
                        </div>

                        <form onSubmit={handleSaveCameraConfig} style={{ padding: '24px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
                            <div>
                                <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: '700', color: '#475569', marginBottom: '6px' }}>
                                    Nombre de la Cámara
                                </label>
                                <input
                                    type="text"
                                    value={configForm.name}
                                    onChange={(e) => setConfigForm({ ...configForm, name: e.target.value })}
                                    required
                                    style={{
                                        width: '100%',
                                        padding: '10px 14px',
                                        borderRadius: '10px',
                                        border: '1px solid #CBD5E1',
                                        fontSize: '0.9rem',
                                        boxSizing: 'border-box'
                                    }}
                                />
                            </div>

                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
                                <div>
                                    <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: '700', color: '#475569', marginBottom: '4px' }}>
                                        Límite Continuo
                                    </label>
                                    <div style={{ position: 'relative' }}>
                                        <input
                                            type="number"
                                            min="1"
                                            max="3600"
                                            value={configForm.maxSeconds}
                                            onChange={(e) => setConfigForm({ ...configForm, maxSeconds: e.target.value })}
                                            required
                                            style={{
                                                width: '100%',
                                                padding: '10px 48px 10px 14px',
                                                borderRadius: '10px',
                                                border: '1px solid #CBD5E1',
                                                fontSize: '0.9rem',
                                                boxSizing: 'border-box'
                                            }}
                                        />
                                        <span style={{ position: 'absolute', right: '12px', top: '10px', fontSize: '0.8rem', color: '#94A3B8', fontWeight: '600' }}>seg</span>
                                    </div>
                                    <span style={{ fontSize: '0.7rem', color: '#94A3B8' }}>Máx 3,600 s (1 hr)</span>
                                </div>

                                <div>
                                    <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: '700', color: '#475569', marginBottom: '4px' }}>
                                        Límite por Hora
                                    </label>
                                    <div style={{ position: 'relative' }}>
                                        <input
                                            type="number"
                                            min="1"
                                            max="60"
                                            value={configForm.maxMinutesPerHour}
                                            onChange={(e) => setConfigForm({ ...configForm, maxMinutesPerHour: e.target.value })}
                                            required
                                            style={{
                                                width: '100%',
                                                padding: '10px 48px 10px 14px',
                                                borderRadius: '10px',
                                                border: '1px solid #CBD5E1',
                                                fontSize: '0.9rem',
                                                boxSizing: 'border-box'
                                            }}
                                        />
                                        <span style={{ position: 'absolute', right: '12px', top: '10px', fontSize: '0.8rem', color: '#94A3B8', fontWeight: '600' }}>min</span>
                                    </div>
                                    <span style={{ fontSize: '0.7rem', color: '#94A3B8' }}>Máx 60 min</span>
                                </div>

                                <div>
                                    <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: '700', color: '#475569', marginBottom: '4px' }}>
                                        Límite por Día
                                    </label>
                                    <div style={{ position: 'relative' }}>
                                        <input
                                            type="number"
                                            min="1"
                                            max="24"
                                            value={configForm.maxHoursPerDay}
                                            onChange={(e) => setConfigForm({ ...configForm, maxHoursPerDay: e.target.value })}
                                            required
                                            style={{
                                                width: '100%',
                                                padding: '10px 48px 10px 14px',
                                                borderRadius: '10px',
                                                border: '1px solid #CBD5E1',
                                                fontSize: '0.9rem',
                                                boxSizing: 'border-box'
                                            }}
                                        />
                                        <span style={{ position: 'absolute', right: '12px', top: '10px', fontSize: '0.8rem', color: '#94A3B8', fontWeight: '600' }}>hrs</span>
                                    </div>
                                    <span style={{ fontSize: '0.7rem', color: '#94A3B8' }}>Máx 24 hrs</span>
                                </div>

                                <div>
                                    <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: '700', color: '#475569', marginBottom: '4px' }}>
                                        Límite por Semana
                                    </label>
                                    <div style={{ position: 'relative' }}>
                                        <input
                                            type="number"
                                            min="1"
                                            max="7"
                                            value={configForm.maxDaysPerWeek}
                                            onChange={(e) => setConfigForm({ ...configForm, maxDaysPerWeek: e.target.value })}
                                            required
                                            style={{
                                                width: '100%',
                                                padding: '10px 48px 10px 14px',
                                                borderRadius: '10px',
                                                border: '1px solid #CBD5E1',
                                                fontSize: '0.9rem',
                                                boxSizing: 'border-box'
                                            }}
                                        />
                                        <span style={{ position: 'absolute', right: '12px', top: '10px', fontSize: '0.8rem', color: '#94A3B8', fontWeight: '600' }}>días</span>
                                    </div>
                                    <span style={{ fontSize: '0.7rem', color: '#94A3B8' }}>Máx 7 días</span>
                                </div>
                            </div>

                            <div>
                                <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: '700', color: '#475569', marginBottom: '8px' }}>
                                    Roles con Acceso Autorizado (Multi-selección)
                                </label>
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                                    {AVAILABLE_ROLES.map((r) => {
                                        const isSelected = configForm.allowedRoles.includes(r.key);
                                        return (
                                            <button
                                                type="button"
                                                key={r.key}
                                                onClick={() => handleToggleRole(r.key)}
                                                style={{
                                                    background: isSelected ? '#7E66FC' : '#F1F5F9',
                                                    color: isSelected ? 'white' : '#475569',
                                                    border: `1.5px solid ${isSelected ? '#7E66FC' : '#E2E8F0'}`,
                                                    borderRadius: '20px',
                                                    padding: '8px 16px',
                                                    fontSize: '0.85rem',
                                                    fontWeight: '600',
                                                    cursor: 'pointer',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: '6px',
                                                    transition: 'all 0.2s',
                                                    boxShadow: isSelected ? '0 2px 8px rgba(126, 102, 252, 0.3)' : 'none'
                                                }}
                                            >
                                                {isSelected && <IoCheckmarkCircle size={16} />}
                                                <span>{r.label}</span>
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>

                            <div>
                                <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: '700', color: '#475569', marginBottom: '6px' }}>
                                    Descripción y Finalidad (Visible a Residentes)
                                </label>
                                <textarea
                                    value={configForm.description}
                                    onChange={(e) => setConfigForm({ ...configForm, description: e.target.value })}
                                    rows={3}
                                    style={{
                                        width: '100%',
                                        padding: '10px 14px',
                                        borderRadius: '10px',
                                        border: '1px solid #CBD5E1',
                                        fontSize: '0.85rem',
                                        boxSizing: 'border-box',
                                        resize: 'none'
                                    }}
                                />
                            </div>

                            <div style={{ display: 'flex', gap: '10px', marginTop: '8px' }}>
                                <button
                                    type="button"
                                    onClick={() => setSelectedCameraForConfig(null)}
                                    style={{
                                        flex: 1,
                                        background: '#F1F5F9',
                                        color: '#475569',
                                        border: 'none',
                                        borderRadius: '12px',
                                        padding: '12px',
                                        fontWeight: '600',
                                        fontSize: '0.9rem',
                                        cursor: 'pointer'
                                    }}
                                >
                                    Cancelar
                                </button>
                                <button
                                    type="submit"
                                    style={{
                                        flex: 1.5,
                                        background: '#7E66FC',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '12px',
                                        padding: '12px',
                                        fontWeight: '600',
                                        fontSize: '0.9rem',
                                        cursor: 'pointer',
                                        boxShadow: '0 4px 10px rgba(126, 102, 252, 0.3)'
                                    }}
                                >
                                    Guardar Cambios
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* ========================================================= */}
            {/* 3. MODAL DE BITÁCORA DE MONITOREO (AUDITORÍA DE VISTAS)    */}
            {/* ========================================================= */}
            {showBitacoraModal && (
                <div style={{
                    position: 'fixed',
                    inset: 0,
                    backgroundColor: 'rgba(15, 23, 42, 0.65)',
                    backdropFilter: 'blur(4px)',
                    zIndex: 9999,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '20px'
                }}>
                    <div style={{
                        background: 'white',
                        borderRadius: '24px',
                        maxWidth: '600px',
                        width: '100%',
                        maxHeight: '85vh',
                        display: 'flex',
                        flexDirection: 'column',
                        overflow: 'hidden',
                        boxShadow: '0 20px 25px -5px rgba(0,0,0,0.2)'
                    }}>
                        <div style={{
                            background: 'linear-gradient(135deg, #1E293B 0%, #0F172A 100%)',
                            color: 'white',
                            padding: '20px',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center'
                        }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                <IoListOutline size={24} color="#7E66FC" />
                                <div>
                                    <h3 style={{ margin: 0, fontSize: '1.05rem', fontWeight: '700' }}>Bitácora de Monitoreo</h3>
                                    <span style={{ fontSize: '0.75rem', color: '#94A3B8' }}>Registro de auditoría de accesos a cámaras</span>
                                </div>
                            </div>
                            <button
                                onClick={() => setShowBitacoraModal(false)}
                                style={{ background: 'none', border: 'none', color: '#94A3B8', cursor: 'pointer' }}
                            >
                                <IoClose size={22} />
                            </button>
                        </div>

                        <div style={{ padding: '20px', overflowY: 'auto', flex: 1, display: 'flex', flexDirection: 'column', gap: '12px' }}>
                            {bitacoraLogs.length === 0 ? (
                                <div style={{ textAlign: 'center', padding: '40px 20px', color: '#64748B' }}>
                                    <IoShieldCheckmarkOutline size={48} color="#CBD5E1" style={{ marginBottom: '12px' }} />
                                    <h4 style={{ margin: 0, fontSize: '1rem', color: '#1E293B' }}>Sin registros aún</h4>
                                    <p style={{ margin: '6px 0 0', fontSize: '0.85rem' }}>
                                        Cada vez que un residente conecte a una cámara, su actividad se reflejará aquí.
                                    </p>
                                </div>
                            ) : (
                                bitacoraLogs.map((log) => (
                                    <div
                                        key={log.id}
                                        style={{
                                            background: '#F8FAFC',
                                            borderRadius: '14px',
                                            padding: '14px 16px',
                                            border: '1px solid #E2E8F0',
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'center'
                                        }}
                                    >
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                            <div style={{
                                                width: '40px',
                                                height: '40px',
                                                borderRadius: '50%',
                                                background: 'rgba(126, 102, 252, 0.1)',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                color: '#7E66FC'
                                            }}>
                                                <IoPersonOutline size={20} />
                                            </div>
                                            <div>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                    <h5 style={{ margin: 0, fontSize: '0.9rem', color: '#0F172A', fontWeight: '700' }}>
                                                        {log.usuarioNombre}
                                                    </h5>
                                                    <span style={{
                                                        background: '#E2E8F0',
                                                        color: '#475569',
                                                        padding: '2px 8px',
                                                        borderRadius: '6px',
                                                        fontSize: '0.7rem',
                                                        fontWeight: '600'
                                                    }}>
                                                        {log.casaDepto}
                                                    </span>
                                                </div>
                                                <span style={{ fontSize: '0.75rem', color: '#64748B' }}>
                                                    {log.camaraNombre} • {log.maxSecondsAsignados}s
                                                </span>
                                            </div>
                                        </div>

                                        <div style={{ textAlign: 'right' }}>
                                            <span style={{ fontSize: '0.8rem', fontWeight: '700', color: '#0F172A', display: 'block' }}>
                                                {log.fechaHora}
                                            </span>
                                            <span style={{ fontSize: '0.7rem', color: '#94A3B8' }}>
                                                {log.fecha}
                                            </span>
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>

                        <div style={{ padding: '16px 20px', borderTop: '1px solid #E2E8F0', background: '#F8FAFC' }}>
                            <button
                                onClick={() => setShowBitacoraModal(false)}
                                style={{
                                    width: '100%',
                                    background: '#7E66FC',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '12px',
                                    padding: '12px',
                                    fontWeight: '600',
                                    fontSize: '0.9rem',
                                    cursor: 'pointer'
                                }}
                            >
                                Cerrar Bitácora
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default CentinelaScreen;
